import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useStartups,
  useInvalidateStartups,
  projectService,
  type StartupDto,
} from '@ideoz/core';
import {
  Button,
  ConfirmProjectModal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Card,
  CardContent,
  cn,
} from '@ideoz/ui';
import { Ellipsis, Folder, FolderMinus, Pencil, Plus, Rocket, Trash2 } from 'lucide-react';

export default function StartupListPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: startups = [], isLoading: isFetching } = useStartups();
  const invalidateStartups = useInvalidateStartups();

  const [startupToDelete, setStartupToDelete] = useState<StartupDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!startupToDelete) return;
    setIsDeleting(true);
    try {
      await projectService.deleteStartup(startupToDelete.id);
      await invalidateStartups();
      setStartupToDelete(null);
    } catch (err) {
      console.error('Failed to delete startup:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen p-4 max-w-landing-page mx-auto py-16 flex flex-col">
      <header className="mb-6 flex flex-col gap-2">
        <h1 className="text-2xl text-start font-semibold text-gray-800">{t('startups.title')}</h1>
        <p className="text-gray-600 text-sm">{t('startups.subtitle')}</p>
      </header>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {
          <Card
            onClick={() => navigate('/onboarding')}
            className={cn(
              'group cursor-pointer border border-dashed min-h-48 rounded-3xl',
              'hover:border-primary hover:bg-blue-light-100/20 transition-all duration-200'
            )}
          >
            <CardContent className="p-6 flex flex-col items-center justify-center h-full min-h-[180px]">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Plus className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-medium lg:font-semibold text-lg text-card-foreground">
                {t('startups.addNew')}
              </h3>
              <p className="text-sm text-muted-foreground text-center mt-2">
                {t('startups.configure')}
              </p>
            </CardContent>
          </Card>
        }
        {isFetching ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          startups.map((startup: StartupDto) => (
            <Card
              key={startup.id}
              onClick={() => navigate(`/${startup.slug}/work-scopes`)}
              className="group hover:shadow-sm transition-all duration-200 cursor-pointer border h-full min-h-48 rounded-3xl"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Rocket className="w-6 h-6 text-primary" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        aria-label={t('startups.actionsAria', { name: startup.name })}
                        data-testid={`startup-actions-${startup.id}`}
                      >
                        <Ellipsis size={18} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="border p-0 shadow-md">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/${startup.slug}/profile`);
                        }}
                        className="gap-2 cursor-pointer"
                      >
                        <Pencil className="h-3.5 w-3.5" /> {t('startups.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setStartupToDelete(startup);
                        }}
                        className="gap-2 cursor-pointer text-red-600 focus:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> {t('startups.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h2 className="font-medium lg:font-semibold text-lg mb-2 text-card-foreground line-clamp-1">
                  {startup.name}
                </h2>
                {(() => {
                  // Description may be rich-text HTML — strip tags for the
                  // card preview and skip rendering when there's nothing left.
                  const preview = (startup.description ?? '')
                    .replace(/<[^>]+>/g, '')
                    .replace(/&nbsp;/g, ' ')
                    .trim();
                  if (!preview) return null;
                  return (
                    <p className="text-sm text-muted-foreground line-clamp-2">{preview}</p>
                  );
                })()}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Startup — type-to-confirm, same pattern as projects/scopes */}
      <ConfirmProjectModal
        isOpen={!!startupToDelete}
        onClose={() => setStartupToDelete(null)}
        onCreate={handleConfirmDelete}
        title={t('startups.deleteDialog.title')}
        info={{
          title: t('startups.deleteDialog.infoTitle'),
          desc: t('startups.deleteDialog.infoDesc'),
        }}
        buttonText={t('startups.deleteDialog.button')}
        buttonType="danger"
        iconName={FolderMinus}
        inputLabel={t('startups.deleteDialog.inputLabel')}
        expectedProjectName={startupToDelete?.name}
        targetAction="delete"
        isLoading={isDeleting}
      />
    </div>
  );
}
